import React, { useState } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Alert, AlertDescription } from './ui/alert';
import { Loader2, Package, MapPin, Clock, DollarSign } from 'lucide-react';
import { colors } from '../theme';

const CotacaoFrete = () => {
  const CEP_ORIGEM_FIXO = '98280-000'; // Matriz Primaza - Panambi/RS
  
  const [formData, setFormData] = useState({
    cep_destino: '',
    peso: '',
    valor_nf: '',
    metragem_cubica: ''
  });
  
  const [cotacoes, setCotacoes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [enderecoDestino, setEnderecoDestino] = useState(null);
  const [cepError, setCepError] = useState('');

  const formatCEP = (value) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 8) {
      return numbers.replace(/(\d{5})(\d{3})/, '$1-$2');
    }
    return numbers.slice(0, 8).replace(/(\d{5})(\d{3})/, '$1-$2');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'cep_destino') {
      const formatted = formatCEP(value);
      setFormData(prev => ({
        ...prev,
        [name]: formatted
      }));
      
      // Buscar CEP automaticamente quando completo
      const cepLimpo = formatted.replace(/\D/g, '');
      if (cepLimpo.length === 8) {
        buscarCEP(cepLimpo);
      } else {
        setEnderecoDestino(null);
      }
    } else if (name === 'peso') {
      // Permitir apenas números e ponto decimal
      const numericValue = value.replace(/[^0-9.]/g, '');
      setFormData(prev => ({
        ...prev,
        [name]: numericValue
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const buscarCEP = async (cep) => {
    setCepError('');
    try {
      const response = await axios.get(`${API}/cep/${cep}`);
      
      if (response.data.erro) {
        setCepError('❌ CEP não encontrado. Verifique o número digitado.');
        setEnderecoDestino(null);
      } else {
        setEnderecoDestino(response.data);
      }
    } catch (error) {
      console.error('Erro ao buscar CEP:', error);
      setCepError('❌ CEP inválido ou não encontrado.');
      setEnderecoDestino(null);
    }
  };

  const handleCEPBlur = (e) => {
    const { name, value } = e.target;
    if (name === 'cep_destino') {
      buscarCEP(value);
    }
  };

  const calcularFrete = async (e) => {
    e.preventDefault();
    setError('');
    setCotacoes([]);
    
    // Validar CEP antes de calcular
    if (!enderecoDestino || cepError) {
      setError('⚠️ Por favor, digite um CEP válido antes de calcular o frete.');
      return;
    }
    
    setLoading(true);

    try {
      // Chamar APIs das transportadoras em paralelo

      // API da Expresso São Miguel
      const cotacaoESM = axios.post(`${API}/esm/cotacao`, {
        cep_destino: formData.cep_destino.replace(/\D/g, ''),
        peso: parseFloat(formData.peso),
        cubagem: formData.metragem_cubica ? parseFloat(formData.metragem_cubica) : 0.1,
        valor_mercadoria: formData.valor_nf ? parseFloat(formData.valor_nf) : 100,
        quantidade: 1,
        tipo_pessoa_destino: 'J',
        tipo_frete: 'CIF'
      }).catch(err => {
        console.error('Erro cotação ESM:', err?.response?.data || err);
        return { data: null, error: err };
      });

      // API da Braspress
      const cotacaoBraspress = axios.post(`${API}/braspress/cotacao-simples`, {
        cep_destino: formData.cep_destino.replace(/\D/g, ''),
        peso: parseFloat(formData.peso),
        valor_mercadoria: formData.valor_nf ? parseFloat(formData.valor_nf) : 100,
        volumes: 1,
        cubagem: formData.metragem_cubica ? {
          altura: 0.5,
          largura: 0.5,
          comprimento: parseFloat(formData.metragem_cubica)
        } : { altura: 0.5, largura: 0.5, comprimento: 0.5 },
        tipo_frete: 'CIF'
      }).catch(err => {
        console.error('Erro cotação Braspress:', err?.response?.data || err);
        return { data: null, error: err };
      });

      // API dos Correios (PAC e SEDEX)
      const cotacaoCorreios = axios.post(`${API}/correios/cotacao`, {
        cep_destino: formData.cep_destino.replace(/\D/g, ''),
        peso: parseFloat(formData.peso),
        comprimento: 20,
        altura: 10,
        largura: 15,
        valor_declarado: formData.valor_nf ? parseFloat(formData.valor_nf) : 0
      }).catch(err => {
        console.error('Erro cotação Correios:', err?.response?.data || err);
        return { data: null, error: err };
      });

      // Aguardar todas as requisições
      const [resESM, resBraspress, resCorreios] = await Promise.all([
        cotacaoESM,
        cotacaoBraspress,
        cotacaoCorreios
      ]);

      let todasCotacoes = [];

      // Adicionar cotação ESM se disponível
      if (resESM.data && resESM.data.success && resESM.data.valor_frete) {
        let prazoDias = 1;
        if (resESM.data.previsao_entrega && resESM.data.previsao_embarque) {
          const dataEntrega = resESM.data.previsao_entrega.split(' ')[0];
          const dataEmbarque = resESM.data.previsao_embarque;
          const [diaE, mesE, anoE] = dataEntrega.split('/');
          const [diaB, mesB, anoB] = dataEmbarque.split('/');
          const entrega = new Date(anoE, mesE - 1, diaE);
          const embarque = new Date(anoB, mesB - 1, diaB);
          prazoDias = Math.max(1, Math.ceil((entrega - embarque) / (1000 * 60 * 60 * 24)));
        }

        todasCotacoes.push({
          transportadora_id: 'esm_api',
          transportadora_nome: 'Expresso São Miguel',
          valor: resESM.data.valor_frete,
          prazo_dias: prazoDias,
          previsao_entrega: resESM.data.previsao_entrega,
          informacao_entrega: resESM.data.informacao_entrega,
          api_real: true
        });
      }

      // Adicionar cotação Braspress se disponível
      if (resBraspress.data && resBraspress.data.success && resBraspress.data.valor_frete) {
        todasCotacoes.push({
          transportadora_id: 'braspress_api',
          transportadora_nome: 'Braspress',
          valor: resBraspress.data.valor_frete,
          prazo_dias: resBraspress.data.prazo_dias,
          cidade_destino: resBraspress.data.cidade_destino,
          uf_destino: resBraspress.data.uf_destino,
          api_real: true
        });
      }

      // Adicionar cotações dos Correios se disponíveis
      if (resCorreios.data && resCorreios.data.success && resCorreios.data.cotacoes) {
        resCorreios.data.cotacoes.forEach(cotacao => {
          // Converter valor de string "20,40" para número 20.40
          let valorFrete = cotacao.valor_frete;
          if (typeof valorFrete === 'string') {
            valorFrete = parseFloat(valorFrete.replace(',', '.'));
          }
          
          // Só adicionar se tiver valor válido
          if (valorFrete && valorFrete > 0) {
            todasCotacoes.push({
              transportadora_id: `correios_${cotacao.servico}`,
              transportadora_nome: `Correios ${cotacao.nome_servico}`,
              valor: valorFrete,
              prazo_dias: cotacao.prazo_dias,
              data_maxima_entrega: cotacao.data_maxima_entrega,
              detalhes: cotacao.detalhes,
              api_real: true
            });
          }
        });
      }

      // Ordenar por valor (filtrando valores inválidos)
      todasCotacoes = todasCotacoes.filter(c => c.valor && c.valor > 0);
      todasCotacoes.sort((a, b) => a.valor - b.valor);

      setCotacoes(todasCotacoes);
      
      if (todasCotacoes.length === 0) {
        setError('Nenhuma transportadora disponível para esta rota.');
      }
    } catch (error) {
      console.error('Erro geral:', error);
      setError(error.response?.data?.detail || 'Erro ao calcular frete. Verifique os dados e tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  return (
    <div className="max-w-2xl mx-auto p-6">
      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="text-xl flex items-center gap-2">
            <Package size={20} style={{ color: colors.primaza.primary }} />
            Cotação de Frete Rápida
          </CardTitle>
          <p className="text-sm text-gray-500 mt-1">
            Origem: Panambi/RS (98280-000)
          </p>
        </CardHeader>
        
        {/* Banner informativo */}
        <div className="mx-6 mb-4">
          <Alert className="border-green-300 bg-green-50">
            <div className="flex items-start gap-3">
              <div className="text-2xl">✅</div>
              <div className="flex-1">
                <AlertDescription className="text-green-800">
                  <p className="font-semibold mb-1">Cotações em Tempo Real</p>
                  <p className="text-sm">
                    Todas as cotações são obtidas diretamente das APIs das transportadoras, 
                    com <strong>valores e prazos atualizados</strong>.
                  </p>
                </AlertDescription>
              </div>
            </div>
          </Alert>
        </div>
        
        <CardContent>
          <form onSubmit={calcularFrete} className="space-y-4">
            {/* CEP Destino */}
            <div>
              <Label htmlFor="cep_destino" className="text-sm">CEP de Destino</Label>
              <Input
                id="cep_destino"
                name="cep_destino"
                value={formData.cep_destino}
                onChange={handleInputChange}
                placeholder="00000-000"
                maxLength={9}
                required
                className="mt-1"
              />
              {enderecoDestino && (
                <p className="text-sm text-gray-600 mt-2 flex items-center gap-1">
                  <MapPin size={14} />
                  {enderecoDestino.localidade} - {enderecoDestino.uf}
                </p>
              )}
              {cepError && (
                <p className="text-sm text-red-600 mt-2">
                  {cepError}
                </p>
              )}
            </div>

            {/* Peso */}
            <div>
              <Label htmlFor="peso" className="text-sm">Peso Total (kg)</Label>
              <Input
                id="peso"
                name="peso"
                type="text"
                value={formData.peso}
                onChange={handleInputChange}
                placeholder="Ex: 5.5"
                required
                className="mt-1"
              />
            </div>

            {/* Campos Opcionais */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="valor_nf" className="text-sm">Valor NF (R$) <span className="text-gray-400">opcional</span></Label>
                <Input
                  id="valor_nf"
                  name="valor_nf"
                  type="number"
                  step="0.01"
                  value={formData.valor_nf}
                  onChange={handleInputChange}
                  placeholder="Ex: 185.00"
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="metragem_cubica" className="text-sm">Metragem m³ <span className="text-gray-400">opcional</span></Label>
                <Input
                  id="metragem_cubica"
                  name="metragem_cubica"
                  type="number"
                  step="0.01"
                  value={formData.metragem_cubica}
                  onChange={handleInputChange}
                  placeholder="Ex: 0.5"
                  className="mt-1"
                />
              </div>
            </div>

            {/* Botão Calcular */}
            <Button
              type="submit"
              disabled={loading}
              className="w-full text-white font-semibold"
              style={{
                backgroundColor: colors.primaza.primary
              }}
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Calculando...
                </>
              ) : (
                'Calcular Frete'
              )}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Resultados Compactos */}
      {cotacoes.length > 0 && (
        <Card className="mt-4">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">
              💰 {cotacoes.length} {cotacoes.length === 1 ? 'opção encontrada' : 'opções encontradas'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {cotacoes.map((cotacao, index) => (
                <div
                  key={`${cotacao.transportadora_id}-${index}`}
                  className="p-3 rounded-lg hover:bg-gray-50 transition-colors border"
                  style={{
                    borderColor: index === 0 ? '#10B981' : '#E5E7EB',
                    backgroundColor: index === 0 ? '#ECFDF5' : 'white'
                  }}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-gray-900 text-sm">
                          {cotacao.transportadora_nome}
                        </p>
                        {index === 0 && (
                          <span className="text-xs bg-green-600 text-white px-2 py-0.5 rounded-full">
                            Melhor preço
                          </span>
                        )}
                      </div>
                      {cotacao.previsao_entrega && (
                        <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                          <Clock size={12} />
                          Entrega: {cotacao.previsao_entrega}
                        </p>
                      )}
                      {cotacao.detalhes && (
                        <p className="text-xs text-gray-400 mt-1">
                          {cotacao.detalhes}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-4 text-right">
                      <div>
                        <span className="text-sm font-semibold text-gray-900">
                          R$ {cotacao.valor.toFixed(2)}
                        </span>
                        <p className="text-xs text-gray-500">
                          {cotacao.prazo_dias} {cotacao.prazo_dias === 1 ? 'dia útil' : 'dias úteis'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <p className="text-xs text-gray-500 mt-3 text-center">
              Valores e prazos obtidos diretamente das transportadoras
            </p>
          </CardContent>
        </Card>
      )}

      {/* Erro */}
      {error && (
        <Alert className="bg-red-50 border-red-200 mt-4">
          <AlertDescription className="text-red-800 text-sm">
            {error}
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
};

export default CotacaoFrete;